import { useLanguage } from '../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Check, ShoppingBag, ArrowRight, Mail, Building2, Copy, AlertTriangle } from 'lucide-react';
import { publicApi, useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function OrderConfirmation() {
  const { t } = useLanguage();
  const { orderNumber } = useParams();
  const [searchParams] = useSearchParams();
  const isBankTransfer = searchParams.get('method') === 'bank_transfer';
  const [bankDetails, setBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (isBankTransfer) {
        try {
          const { data } = await publicApi.get('/bank-details');
          setBankDetails(data);
        } catch (err) {
          console.error('Error fetching bank details:', err);
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [isBankTransfer]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copié !`);
    }).catch(() => {
      toast.error('Erreur de copie');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Bank Transfer confirmation page
  if (isBankTransfer) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 px-4 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-xl" data-testid="bank-transfer-success-icon">
              <Check size={56} className="text-white" strokeWidth={3} />
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3" data-testid="bank-transfer-title">
              Merci pour votre commande !
            </h1>
            <p className="text-gray-500 text-lg">
              Votre commande <span className="font-bold text-gray-900">{orderNumber}</span> a bien été enregistrée
            </p>
          </div>

          {/* Warning: pending payment */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 mb-6" data-testid="pending-payment-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-900 text-lg mb-1">
                  En attente de paiement
                </p>
                <p className="text-amber-800 text-sm leading-relaxed">
                  Votre commande ne sera expédiée qu'une fois le virement bancaire reçu. 
                  Veuillez effectuer le virement dans les meilleurs délais.
                </p>
              </div>
            </div>
          </div>

          {/* Email sent notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6" data-testid="email-sent-notice">
            <div className="flex items-start gap-3">
              <Mail size={22} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-blue-900 mb-1">Email envoyé</p>
                <p className="text-sm text-blue-700">
                  Un email avec les coordonnées bancaires et les détails de votre commande vous a été envoyé. 
                  Pensez à vérifier vos spams.
                </p>
              </div>
            </div>
          </div>

          {/* Bank Details Card */}
          {bankDetails && (bankDetails.iban || bankDetails.account_holder) && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8 mb-6" data-testid="bank-details-card">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="w-12 h-12 rounded-full bg-[#00aaff]/10 flex items-center justify-center">
                  <Building2 size={24} className="text-[#00aaff]" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">Coordonnées bancaires</p>
                  <p className="text-sm text-gray-500">Effectuez votre virement avec ces informations</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Account Holder */}
                {bankDetails.account_holder && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Titulaire du compte</p>
                      <p className="font-bold text-gray-900 text-lg">{bankDetails.account_holder}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(bankDetails.account_holder, 'Titulaire')}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Copier"
                    >
                      <Copy size={18} className="text-gray-500" />
                    </button>
                  </div>
                )}

                {/* Bank Name */}
                {bankDetails.bank_name && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Banque</p>
                      <p className="font-bold text-gray-900">{bankDetails.bank_name}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(bankDetails.bank_name, 'Banque')}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Copier"
                    >
                      <Copy size={18} className="text-gray-500" />
                    </button>
                  </div>
                )}

                {/* IBAN */}
                {bankDetails.iban && (
                  <div className="flex items-center justify-between p-4 bg-[#00aaff]/5 rounded-xl border border-[#00aaff]/20">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">IBAN</p>
                      <p className="font-mono font-bold text-gray-900 text-lg tracking-wider">{bankDetails.iban}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(bankDetails.iban, 'IBAN')}
                      className="p-2 hover:bg-[#00aaff]/10 rounded-lg transition-colors"
                      title="Copier l'IBAN"
                    >
                      <Copy size={18} className="text-[#00aaff]" />
                    </button>
                  </div>
                )}

                {/* BIC */}
                {bankDetails.bic && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">BIC / SWIFT</p>
                      <p className="font-mono font-bold text-gray-900 tracking-wider">{bankDetails.bic}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(bankDetails.bic, 'BIC')}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Copier le BIC"
                    >
                      <Copy size={18} className="text-gray-500" />
                    </button>
                  </div>
                )}

                {/* Reference */}
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div>
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Référence du virement</p>
                    <p className="font-mono font-bold text-amber-900 text-lg">{orderNumber}</p>
                    <p className="text-xs text-amber-600 mt-1">Indiquez ce numéro en référence de votre virement</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(orderNumber, 'Référence')}
                    className="p-2 hover:bg-amber-100 rounded-lg transition-colors"
                    title="Copier la référence"
                  >
                    <Copy size={18} className="text-amber-600" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/collection"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white border-2 border-gray-200 text-gray-700 font-bold hover:border-[#00aaff] hover:text-[#00aaff] transition-all"
              data-testid="continue-shopping-link"
            >
              <ShoppingBag size={20} />
              Continuer mes achats
            </Link>
            
            {isAuthenticated && (
              <Link
                to="/mes-commandes"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-[#00aaff] to-[#0088dd] text-white font-bold hover:shadow-lg transition-all"
                data-testid="my-orders-link"
              >
                Voir mes commandes
                <ArrowRight size={20} />
              </Link>
            )}
          </div>

          {!isAuthenticated && (
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
              <p className="text-sm text-gray-700 mb-3">
                <strong>Astuce :</strong> Créez un compte pour suivre vos commandes facilement
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-purple-700 font-semibold text-sm hover:shadow-md transition-all"
              >
                Créer un compte gratuit
                <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default confirmation page (card payment / Stripe)
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-xl">
            <Check size={56} className="text-white" strokeWidth={3} />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            Commande confirmée !
          </h1>
          <p className="text-gray-500 text-lg">
            Merci pour votre commande
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
            <div className="w-12 h-12 rounded-full bg-[#00aaff]/10 flex items-center justify-center">
              <ShoppingBag size={24} className="text-[#00aaff]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Numéro de commande</p>
              <p className="text-xl font-black text-gray-900">{orderNumber}</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <Mail size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-blue-900 mb-1">Email de confirmation envoyé</p>
              <p className="text-sm text-blue-700">
                Vous allez recevoir un email de confirmation avec les détails de votre commande.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/collection"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white border-2 border-gray-200 text-gray-700 font-bold hover:border-[#00aaff] hover:text-[#00aaff] transition-all"
          >
            <ShoppingBag size={20} />
            Continuer mes achats
          </Link>
          
          {isAuthenticated && (
            <Link
              to="/mes-commandes"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-[#00aaff] to-[#0088dd] text-white font-bold hover:shadow-lg transition-all"
            >
              Voir mes commandes
              <ArrowRight size={20} />
            </Link>
          )}
        </div>

        {!isAuthenticated && (
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
            <p className="text-sm text-gray-700 mb-3">
              <strong>Astuce :</strong> Créez un compte pour suivre vos commandes facilement
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-purple-700 font-semibold text-sm hover:shadow-md transition-all"
            >
              Créer un compte gratuit
              <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
